// Utilidad para generar y descargar un archivo KML a partir de posiciones históricas
// Cada posición debe contener al menos: latitude, longitude, gpsDate (ISO), locationName, speed, eventName
// Uso: generateAndDownloadKML(positions, { unitName, from, to, parkedPositions })

interface HistoricalPositionLike {
  latitude: number;
  longitude: number;
  gpsDate?: string;
  locationName?: string;
  speed?: number;
  eventName?: string;
  eventId?: number | null;
  endDate?: string;
}

interface KMLMeta {
  unitName?: string;
  from?: string; // ISO
  to?: string; // ISO
  parkedPositions?: HistoricalPositionLike[];
}

const escapeXml = (value: any) =>
  (value ?? '')
    .toString()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

function buildPlacemark(p: HistoricalPositionLike, idx: number, styleId: string): string {
  const coords = `${p.longitude},${p.latitude},0`;
  const when = p.gpsDate ? `<TimeStamp><when>${escapeXml(p.gpsDate)}</when></TimeStamp>` : '';
  const hasEvent = p.eventId != null && p.eventId != 0;
  const label = hasEvent ? `${idx + 1} - ${p.eventName}` : `${idx + 1}`;
  const details: Record<string, any> = {
    Location: p.locationName,
    Event: p.eventName,
    Speed: p.speed,
    Index: idx + 1
  };
  if (p.endDate) details['End Date'] = p.endDate;
  const extended = Object.entries(details)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `<Data name="${escapeXml(k)}"><value>${escapeXml(v)}</value></Data>`)
    .join('');
  return `
        <Placemark>
          <name>${escapeXml(label)}</name>
          ${when}
          <styleUrl>#${styleId}</styleUrl>
          <ExtendedData>${extended}</ExtendedData>
          <Point><coordinates>${coords}</coordinates></Point>
        </Placemark>`;
}

export function buildKML(positions: HistoricalPositionLike[], meta: KMLMeta = {}): string {
  const byDate = (a: HistoricalPositionLike, b: HistoricalPositionLike) =>
    new Date(a.gpsDate ?? 0).getTime() - new Date(b.gpsDate ?? 0).getTime();

  const safePositions = (positions || [])
    .filter((p) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number')
    .sort(byDate);
  const safeParked = (meta.parkedPositions || [])
    .filter((p) => p && typeof p.latitude === 'number' && typeof p.longitude === 'number')
    .sort(byDate);

  const header = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  const kmlOpen = `<kml xmlns="http://www.opengis.net/kml/2.2"><Document>`;
  const name = `<name>${escapeXml(
    `Historico ${meta.unitName || ''} ${meta.from || ''} ${meta.to || ''}`.trim()
  )}</name>`;
  const description = `<description>${escapeXml(
    `Historical positions export${meta.unitName ? ' - ' + meta.unitName : ''}`
  )}</description>`;

  const styles = `
    <Style id="lineStyle"><LineStyle><color>ff0055ff</color><width>3</width></LineStyle></Style>
    <Style id="pointStyle"><IconStyle><color>ff0055ff</color><scale>0.8</scale><Icon><href>http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png</href></Icon></IconStyle></Style>
    <Style id="parkedStyle"><IconStyle><color>ff00aaff</color><scale>1.0</scale><Icon><href>http://maps.google.com/mapfiles/kml/paddle/P.png</href></Icon></IconStyle></Style>
  `;

  const allSorted = [
    ...safePositions.map((p) => ({ p, styleId: 'pointStyle' })),
    ...safeParked.map((p) => ({ p, styleId: 'parkedStyle' }))
  ].sort((a, b) => new Date(a.p.gpsDate ?? 0).getTime() - new Date(b.p.gpsDate ?? 0).getTime());

  const positionsFolder = allSorted.length
    ? `<Folder><name>Posiciones</name>${allSorted.map(({ p, styleId }, i) => buildPlacemark(p, i, styleId)).join('\n')}</Folder>`
    : '';

  const lineString = safePositions.length
    ? `
      <Placemark>
        <name>Trayectoria</name>
        <styleUrl>#lineStyle</styleUrl>
        <LineString>
          <tessellate>1</tessellate>
          <coordinates>
            ${safePositions.map((p) => `${p.longitude},${p.latitude},0`).join(' ')}
          </coordinates>
        </LineString>
      </Placemark>`
    : '';

  const kmlClose = `</Document></kml>`;
  return [
    header,
    kmlOpen,
    name,
    description,
    styles,
    positionsFolder,
    lineString,
    kmlClose
  ]
    .filter(Boolean)
    .join('\n');
}

export function downloadKML(kmlString: string, fileName = 'historico.kml') {
  const blob = new Blob([kmlString], { type: 'application/vnd.google-earth.kml+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
}

export function generateAndDownloadKML(
  positions: HistoricalPositionLike[],
  meta: KMLMeta = {},
  fileName?: string
) {
  const kml = buildKML(positions, meta);
  const finalName =
    fileName || `historico_${meta.unitName || 'unidad'}_${meta.from || ''}_${meta.to || ''}.kml`;
  downloadKML(kml, finalName.replace(/\s+/g, '_'));
}

export default generateAndDownloadKML;
